import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  paymentMethodsService,
  BrandSupportedPaymentMethod,
  PaymentMethod,
} from "@/services/paymentMethods.service";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

interface BrandPayoutMethodsEditorProps {
  brandDetailId: string;
  /** Called after a successful save with the new method ids. */
  onSaved?: (paymentMethodIds: string[]) => void;
  onCancel?: () => void;
  /** Hide the Cancel button (e.g. when the editor is the whole card). */
  hideCancel?: boolean;
  /** Optional lead-in above the list. */
  intro?: string;
  saveLabel?: string;
}

/**
 * The one place a brand picks how it pays affiliates. Reused by the Programs
 * page card, the Payouts page prompt and the dashboard banner so the rule
 * (at least one method) and the copy live in one component.
 */
export function BrandPayoutMethodsEditor({
  brandDetailId,
  onSaved,
  onCancel,
  hideCancel = false,
  intro,
  saveLabel = "Save changes",
}: BrandPayoutMethodsEditorProps) {
  const queryClient = useQueryClient();

  const { data: supported = [] } = useQuery({
    queryKey: ["brandSupportedPaymentMethods", brandDetailId],
    queryFn: () => paymentMethodsService.getBrandSupportedPaymentMethods(brandDetailId),
    enabled: !!brandDetailId,
  });

  const { data: available = [], isLoading: isLoadingAvailable } = useQuery({
    queryKey: ["allPaymentMethods"],
    queryFn: () => paymentMethodsService.getAllPaymentMethods(),
  });

  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setSelected(
      (supported as BrandSupportedPaymentMethod[]).map((pm) => pm.paymentMethodId)
    );
  }, [supported]);

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: (ids: string[]) => paymentMethodsService.updateBrandPaymentMethods(ids),
    onSuccess: (_data, ids) => {
      toast.success("Payout methods saved");
      queryClient.invalidateQueries({ queryKey: ["brandSupportedPaymentMethods", brandDetailId] });
      onSaved?.(ids);
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Failed to save payout methods";
      toast.error(message);
    },
  });

  const handleSave = () => {
    if (selected.length < 1) {
      toast.error("Choose at least one payout method");
      return;
    }
    save(selected);
  };

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-4" data-testid="brand-payout-methods-editor">
      <p className="text-xs text-muted-foreground">
        {intro ??
          "Choose the ways you pay affiliates. One is enough to start; affiliates only see methods you support."}
      </p>
      {isLoadingAvailable ? (
        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <Spinner size="sm" /> Loading payout methods...
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {(available as PaymentMethod[])
            .filter((method) => method.isActive)
            .map((method) => {
              const isSelected = selected.includes(method.id);
              return (
                <div
                  key={method.id}
                  className="flex items-center border border-border rounded-md p-3 bg-card cursor-pointer hover:bg-accent"
                  onClick={() => toggle(method.id)}
                >
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(method.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="sr-only"
                      id={`brand-payout-method-${method.id}`}
                      aria-label={method.name}
                    />
                    <div
                      className={`h-5 w-5 flex items-center justify-center border ${
                        isSelected ? "bg-brand-600 border-brand-600" : "border-border bg-card"
                      } rounded-full`}
                    >
                      {isSelected && (
                        <svg className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <label
                    htmlFor={`brand-payout-method-${method.id}`}
                    className="ml-3 block text-sm font-medium text-muted-foreground flex-grow cursor-pointer"
                    onClick={(e) => e.preventDefault()}
                  >
                    {method.name}
                  </label>
                </div>
              );
            })}
        </div>
      )}
      <div className="flex flex-col space-y-2 pt-2 border-t border-border">
        <Button onClick={handleSave} disabled={isSaving || selected.length < 1}>
          {isSaving && <Spinner size="sm" className="border-white" />}
          <span>{saveLabel}</span>
        </Button>
        {!hideCancel && onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
